import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';

/**
 * ProductCard - Tinder-style swipeable card
 * 
 * Props:
 * - item: { type: 'rod' | 'product', product: {...}, reward?: {...} }
 * - onDismiss: () => void - called when dismissed (left swipe)
 * - onReview: () => void - called when review button clicked
 * - animateOut: 'left' | 'right' | null - trigger exit animation externally
 * - onAnimateOutComplete: () => void - called when exit animation finishes
 * - stackPosition: 0 = front, 1 = back, etc.
 */

const truncateDescription = (text, maxLength = 100) => {
  if (!text) return null;
  const cleaned = text.trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + '...';
};

const ProductCard = ({ 
  item, 
  onDismiss, 
  onReview, 
  animateOut = null,
  onAnimateOutComplete,
  stackPosition = 0 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [exitDirection, setExitDirection] = useState(null);
  const cardRef = useRef(null);

  const product = item.product;
  const images = product.images || [];
  const currentImage = images[currentImageIndex];
  const hasMultipleImages = images.length > 1;
  const isInteractive = stackPosition === 0 && !exitDirection;
  const descriptionPreview = truncateDescription(product.description);

  const reward = item.type === 'rod' && item.reward?.amount
    ? `${item.reward.amount} ${item.reward.currency || 'pts'}`
    : null;

  // Handle external animation trigger
  useEffect(() => {
    if (animateOut && !exitDirection) {
      setExitDirection(animateOut);
    }
  }, [animateOut, exitDirection]);

  const handleDismiss = () => {
    if (!isInteractive) return;
    setExitDirection('left');
  };

  const handleAnimationEnd = (e) => {
    // Only handle our card's animation, not children
    if (e.target !== cardRef.current) return;
    
    if (exitDirection === 'left') {
      onDismiss?.();
    } else if (exitDirection === 'right') {
      onAnimateOutComplete?.();
    }
  };

  const handleReviewSwipe = () => {
    if (!isInteractive) return;
    onReview?.();
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleDismiss,
    onSwipedRight: handleReviewSwipe,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const nextImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Build class list
  const classes = ['card'];
  if (stackPosition > 0) classes.push('card--stacked');
  if (exitDirection) classes.push(`card--exit-${exitDirection}`);

  return (
    <div
      ref={cardRef}
      className={classes.join(' ')}
      style={{ '--stack-index': stackPosition }}
      onAnimationEnd={handleAnimationEnd}
      {...(isInteractive ? swipeHandlers : {})}
    >
      <div className="card__image">
        {currentImage ? (
          <img src={currentImage.image} alt="" draggable={false} />
        ) : (
          <div className="card__placeholder" />
        )}

        {hasMultipleImages && (
          <div className="card__dots">
            {images.map((_, i) => (
              <span
                key={i}
                className={`card__dot ${i === currentImageIndex ? 'card__dot--active' : ''}`}
              />
            ))}
          </div>
        )}

        {hasMultipleImages && isInteractive && (
          <>
            <button
              className="card__nav card__nav--prev"
              onClick={prevImage}
              disabled={currentImageIndex === 0}
              aria-label="Previous"
            />
            <button
              className="card__nav card__nav--next"
              onClick={nextImage}
              disabled={currentImageIndex === images.length - 1}
              aria-label="Next"
            />
          </>
        )}

        {reward && (
          <div className="card__reward">{reward}</div>
        )}
      </div>

      <div className="card__info">
        <span className="card__brand">{product.brand_name}</span>
        <h2 className="card__name">{product.name}</h2>
        {descriptionPreview && (
          <p className="card__description">{descriptionPreview}</p>
        )}
      </div>

      <div className="card__actions">
        <button
          className="card__btn card__btn--skip"
          onClick={handleDismiss}
          disabled={!isInteractive}
          aria-label="Skip"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <button
          className="card__btn card__btn--review"
          onClick={onReview}
          disabled={!isInteractive}
          aria-label="Review"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
